import type { BasketAttributesType } from "@onestore/onestore-store-common/api/shapes"
import type {
  PriceTypeValues,
  PriceWithOptionalTaxRate as PriceValue,
} from "@core/types"
import type {
  MicrosoftSpecialQualifications,
  MicrosoftTenantType,
} from "~/store/office/constants"
import type { Account } from "./account"
import type { BasketResponse } from "./basket"
import type { DomainCheck } from "./domainSearch"

export type TaxRate = number // 23
export type TaxValue = number // 0.23
export type CurrencySign = string // zł, €
export type CurrencyCode = string // PLN, EUR
export type PeriodName = "0d" | "2m" | "1m" | "1y" | string
export type PeriodType = number
export type PeriodValue = number

export type ProductAlias = string
export type PlanId = string | number
export type PlanPeriodId = string | number
export type PlanPeriodNumericId = number
export type ResourceId = number

export type SessionResponse = {
  id: string
  acccount_id: number
  user_id: number
  expire_at: string
  created_at: string
}

export type Price = {
  netto: number
  gross: number
  taxRate: TaxRate
  currency: string
}

export type Query = {
  [key: string]: string | null
}

export type JSONResponse = Record<string, unknown>

export type NoContentResponse = never

export type BasketPatchResource = {
  id?: number
  resource?: number
  quantity: number
}

export type BasketPatchResourceWithResourceId = {
  resource: number
  quantity: number
}

export type BasketPatchItem = {
  plan: number
  planPeriod?: number
  parameters?: Record<string, string | number | boolean | null>
  resources?: BasketPatchResource[]
  children?: BasketPatchItem[]
  ignoreHack?: boolean
  parent?: number
  quantity: number
  placeholderId?: string
}

export type CalculateBasketPayload = {
  items: BasketPatchItem[]
  promo_code?: string | null
}

export type ProductPriceResponse = {
  regularPrice: PriceValue
  promoPrice: PriceValue | null
  renewalPrice: PriceValue | null
  discount: number | null
  periodValue: PeriodValue | null
  periodType: PeriodType | null
  periodName: PeriodName | null
  lowestPurchasePrice: PriceValue | null
}

export type UserDataResponse = {
  basket: BasketResponse
  user?: Account
  attributes: BasketAttributesType
}

export type AccountType = "person" | "company"

export type NullableAccountType = AccountType | null

// Api Types

export type JSONPatch = {
  op: "add" | "remove" | "replace"
  path?: string
  value?: unknown
}

export type Parameter = Record<string, unknown>

export type Resource = {
  id: number
  uuid?: string // TODO change to required after API is the same on all brands
  setup_fee_changed_per_unit: boolean
  alias: string
  name: string
  description: string
  measure_unit: string
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  min_value?: number | never
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  max_value?: number | never
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  included_value?: number | never
}

export type DomainExtension = string

export type DomainTransferDefinition = {
  extension: DomainExtension
  id: number
  price_cession: null | number
  price_recover: null | number
  price_transfer: null | number
  authinfo_required: boolean
}

export type CountryCode = string // PLN, EUR

export type CheckUserPasswordResponse = {
  signature: string
  session?: SessionResponse
  hitId?: string
  accountId?: string
}

export type AccountMessage = {
  id: string
  message: string
  type: "info" | string
  class?: "CITY_TYPO" | string
}

export type TaxNumberData = {
  type: "vat" | "regon"
  id: string
  eu_id?: string
  country_code: string
}

export type PhoneNumberData = {
  country_code: string
  number: string
}

export enum TermType {
  EXTERNAL = "external",
  INTERNAL = "internal",
}

export type Term = {
  id: number
  name: string
  optional: boolean
  text: string
  type: TermType
  checked: boolean[]
}

export type BasketTerm = {
  id: number
  name: string
  optional: boolean
  text: string
  type: TermType
  checked: boolean
}

export type TermResponse = Term[]

export type DomainBundlePricing = {
  register_price: number
  register_price_gross: number
  regular_price: number
  regular_price_gross: number
  renewal_price: number
  renewal_price_gross: number
  savings: number
  period_type: PeriodType
  period_value: PeriodValue
}

export type CheckMainDomainResponse = {
  alternative: DomainCheck.Result | null
  premium: DomainCheck.Result | null
  special: DomainCheck.Result | null
  bundles: DomainCheck.DomainSearchBundle[]
  domain: DomainCheck.Result
  unavailable_extensions: DomainCheck.Extension[]
  phrase: DomainCheck.Phrase
}

export type CheckDomainsResponse = DomainCheck.Result[]
export type CheckSuggestionsResponse = DomainCheck.Result[]

export type InvoiceOrderApiFormError = {
  errors?: string[]
}

export type InvoiceOrderApiResponse = {
  status: number
  errors: string[]
  form: {
    children: {
      accountId: InvoiceOrderApiFormError
      email: InvoiceOrderApiFormError
      from: InvoiceOrderApiFormError
      to: InvoiceOrderApiFormError
      type: InvoiceOrderApiFormError
      terms: InvoiceOrderApiFormError
    }
  }
}

type PmailDomain = string
type PmailStatus = "Available"

export type CheckPmailResponseItem = {
  plan_id: PlanId
  alias: string
  priority: number
  is_promoted: boolean
  name: string
  domain: PmailDomain
  status: PmailStatus
}

export type CheckPmailResponse = {
  results: CheckPmailResponseItem[]
  phrase: string
  phraseSanitized: string
}

export type CheckMicrosoftSubdomainResponse = {
  can_continue: boolean
  has_error: string
  domain: boolean
}

export type JSONData = Record<string, unknown>

export type PaymentModalData = {
  checkoutScriptSection: string
  checkoutScriptHtml: string
  checkoutScriptHandler: string | null | undefined
  checkoutScriptSrc: string | null | undefined
}

export type MicrosoftAccountCheckInput = {
  tenant: MicrosoftTenantType
  domain: string
  special_qualifications: MicrosoftSpecialQualifications
}

export type ProductPriceDefinition = {
  regularPrice: PriceTypeValues
  promoPrice: PriceTypeValues | null
  renewalPrice: PriceTypeValues | null
  discount: number | null
  periodValue: PeriodValue | null
  periodType: PeriodType | null
  periodName: PeriodName | null
}

export type FetchCalculateProductsPayload = {
  [key: string]: {
    items: BasketPatchItem[]
  }
}

export type FetchCalculateProductsResponse = {
  [key: string]: ProductPriceResponse
}
